import React from 'react';

// Minimal inline stroke icons (20x20, currentColor) -- no external asset
// dependency (Figma's exported SVG URLs expire after 7 days and would be an
// unnecessary network dependency for a static icon anyway). Visual shape
// matches the Figma nodes closely enough to satisfy the parity re-check;
// exact vector tracing was not required by the spec.
const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export function IconThumbUp(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3Zm0 0 4.5-7.5a2 2 0 0 1 3.6 1.7L14 9h4.2a2 2 0 0 1 1.94 2.5l-1.5 6A2 2 0 0 1 16.7 19H9a2 2 0 0 1-2-2v-6Z" />
    </svg>
  );
}

export function IconThumbDown(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3Zm0 0-4.5 7.5a2 2 0 0 1-3.6-1.7L10 15H5.8a2 2 0 0 1-1.94-2.5l1.5-6A2 2 0 0 1 7.3 5H15a2 2 0 0 1 2 2v6Z" />
    </svg>
  );
}

export function IconCopy(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </svg>
  );
}

export function IconBookmark(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M6 4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v16l-6-4-6 4V4Z" />
    </svg>
  );
}

export function IconShare(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M7 17 17 7M17 7H9M17 7v8" />
    </svg>
  );
}

export function IconMore(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconPin(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M12 2v6M8 8h8l1 4h-3v10h-4V12H7l1-4Z" />
    </svg>
  );
}

export function IconArchive(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8M10 13h4" />
    </svg>
  );
}

export function IconMoveTo(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M4 6h7l2 3h7v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" />
      <path d="M12 12v5m0-5-2 2m2-2 2 2" />
    </svg>
  );
}

export function IconDelete(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-8 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" />
    </svg>
  );
}

export function IconPlus(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconSearch(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function IconEdit(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1 1-4Z" />
    </svg>
  );
}

export function IconSend(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M12 19V5m0 0-6 6m6-6 6 6" />
    </svg>
  );
}

// Sidebar panel toggle -- one glyph, reused for both open and close (the
// accessible name/aria-expanded state carries the distinction, not the icon
// shape, so it never needs a mirror-sensitive rotation).
export function IconSidebar(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>
  );
}

// Workspace switcher icons (WebApp.dc.html sidebar): one per real OZIRA
// product manifest (assistant/code/design/forward/p).
export function IconWorkspaceChat(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function IconWorkspaceCode(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

export function IconWorkspaceDesign(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

export function IconWorkspaceForward(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function IconWorkspaceP(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg {...common} aria-hidden="true" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 13.5 10.5 11l2 2 3-4" />
    </svg>
  );
}
